import { CartItem, ItemType, Sale, StockValidationResult } from '../types';
import { calculateStoredPriceBreakdown } from '../utils/salesPricing';

export interface ShoppingCartState {
  currentSale: Sale | null;
  cartItems: CartItem[];
  selectedCustomer: import('../types').Customer | null;
  isLoading: boolean;
  error: string | null;
  stockValidation: { [itemId: string]: StockValidationResult };
}

export type ShoppingCartAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CURRENT_SALE'; payload: Sale | null }
  | { type: 'SET_SELECTED_CUSTOMER'; payload: import('../types').Customer | null }
  | { type: 'ADD_ITEM'; payload: CartItem }
  | { type: 'UPDATE_ITEM_QUANTITY'; payload: { itemId: string; quantity: number; batchId?: string; unitPrice?: number } }
  | { type: 'REMOVE_ITEM'; payload: { itemId: string; batchId?: string; unitPrice?: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'RESET_SALE' }
  | { type: 'SET_STOCK_VALIDATION'; payload: { [itemId: string]: StockValidationResult } }
  | { type: 'UPDATE_ITEM_BATCH_INFO'; payload: { itemId: string; batchId?: string; batchNumber: string } }
  | { type: 'SYNC_WITH_SALE'; payload: Sale };

export const initialState: ShoppingCartState = {
  currentSale: null,
  cartItems: [],
  selectedCustomer: null,
  isLoading: false,
  error: null,
  stockValidation: {},
};

export function shoppingCartReducer(state: ShoppingCartState, action: ShoppingCartAction): ShoppingCartState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_CURRENT_SALE':
      return { ...state, currentSale: action.payload };
    case 'SET_SELECTED_CUSTOMER':
      return { ...state, selectedCustomer: action.payload };
    case 'ADD_ITEM': {
      const existingItemIndex = state.cartItems.findIndex(
        item => item.itemId === action.payload.itemId &&
          (item.batchId === action.payload.batchId || (!item.batchId && !action.payload.batchId)) &&
          (action.payload.itemType !== ItemType.SERVICE
            || item.unitPrice === action.payload.unitPrice)
      );
      if (existingItemIndex >= 0) {
        const updatedItems = [...state.cartItems];
        const quantity = updatedItems[existingItemIndex].quantity + action.payload.quantity;
        const pricing = calculateStoredPriceBreakdown(
          action.payload.unitNetPrice,
          action.payload.unitPrice,
          action.payload.vatRate,
          quantity,
        );
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          unitPrice: action.payload.unitPrice,
          quantity,
          netAmount: pricing.netAmount,
          vatAmount: pricing.vatAmount,
          totalPrice: pricing.grossAmount,
        };
        return { ...state, cartItems: updatedItems };
      }
      return { ...state, cartItems: [...state.cartItems, action.payload] };
    }
    case 'UPDATE_ITEM_QUANTITY':
      return {
        ...state,
        cartItems: state.cartItems.map(item => {
          if (item.itemId === action.payload.itemId &&
          (item.batchId === action.payload.batchId || (!item.batchId && !action.payload.batchId)) &&
          (action.payload.unitPrice === undefined || item.unitPrice === action.payload.unitPrice)) {
            const pricing = calculateStoredPriceBreakdown(
              item.unitNetPrice,
              item.unitPrice,
              item.vatRate,
              action.payload.quantity,
            );
            return {
              ...item,
              quantity: action.payload.quantity,
              netAmount: pricing.netAmount,
              vatAmount: pricing.vatAmount,
              totalPrice: pricing.grossAmount,
            };
          }
          return item;
        }),
      };
    case 'REMOVE_ITEM':
      return {
        ...state,
        cartItems: state.cartItems.filter(item =>
          !(item.itemId === action.payload.itemId &&
            (item.batchId === action.payload.batchId || (!item.batchId && !action.payload.batchId)) &&
            (action.payload.unitPrice === undefined || item.unitPrice === action.payload.unitPrice))
        ),
      };
    case 'CLEAR_CART':
      // Keep the selected customer while starting a fresh basket for them.
      return { ...state, cartItems: [], currentSale: null, stockValidation: {}, error: null };
    case 'RESET_SALE':
      // Start a completely new sale, including customer and local cart UI state.
      return { ...initialState };
    case 'SET_STOCK_VALIDATION':
      return { ...state, stockValidation: action.payload };
    case 'UPDATE_ITEM_BATCH_INFO':
      return {
        ...state,
        cartItems: state.cartItems.map(item =>
          item.itemId === action.payload.itemId &&
          (item.batchId === action.payload.batchId || (!item.batchId && !action.payload.batchId))
            ? { ...item, batchNumber: action.payload.batchNumber }
            : item
        ),
      };
    case 'SYNC_WITH_SALE': {
      const cartItems: CartItem[] = (action.payload.saleItems || []).map(saleItem => ({
        itemId: saleItem.itemId,
        itemName: saleItem.itemName,
        quantity: saleItem.quantity,
        unitPrice: saleItem.unitPrice,
        enteredUnitPrice: saleItem.unitPrice,
        unitNetPrice: saleItem.unitNetPrice ?? saleItem.unitPrice,
        vatRate: saleItem.vatRate ?? 0,
        netAmount: saleItem.netAmount ?? saleItem.totalPrice,
        vatAmount: saleItem.vatAmount ?? 0,
        totalPrice: saleItem.totalPrice,
        batchId: saleItem.batchId,
        batchNumber: saleItem.batchNumber,
        availableStock: 0,
        enableBatchTracking: false,
      }));
      return { ...state, currentSale: action.payload, cartItems, selectedCustomer: null };
    }
    default:
      return state;
  }
}
